import React from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import ForumOutlined from '@mui/icons-material/ForumOutlined';
import HomeWorkOutlined from '@mui/icons-material/HomeWorkOutlined';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';

const principles = [
  {
    icon: <LocationOnOutlined />,
    title: 'Local knowledge',
    text: 'Practical guidance shaped by the Middlesbrough and Teesside property market.',
  },
  {
    icon: <ForumOutlined />,
    title: 'Clear communication',
    text: 'Straightforward updates and advice, without unnecessary jargon or pressure.',
  },
  {
    icon: <TaskAltOutlined />,
    title: 'Dependable service',
    text: 'One local office coordinating each move, tenancy and managed property.',
  },
];

export default function AboutPage() {
  return (
    <Box component="main" sx={{ bgcolor: '#fff' }}>
      <Box
        component="section"
        sx={{
          position: 'relative',
          color: '#fff',
          backgroundImage:
            'linear-gradient(90deg, rgba(7,18,36,.92), rgba(7,18,36,.62)), url(/media/hero-property-poster.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Typography variant="overline" sx={{ color: 'rgba(255,255,255,.72)', letterSpacing: '.14em' }}>
            About MRM Lettings
          </Typography>
          <Typography component="h1" sx={{ mt: 1.5, maxWidth: 730, fontSize: { xs: '2.6rem', md: '4.3rem' }, fontWeight: 800, lineHeight: 1.02, letterSpacing: '-.045em' }}>
            Local property advice, delivered properly.
          </Typography>
          <Typography sx={{ mt: 2.5, maxWidth: 650, color: 'rgba(255,255,255,.82)', fontSize: { xs: '1.05rem', md: '1.18rem' }, lineHeight: 1.7 }}>
            A family-led property company built on honest advice, personal service and genuine local knowledge.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
          <Grid size={{ xs: 12, md: 7 }}>
            <Typography variant="overline" color="primary.main">Our story</Typography>
            <Typography variant="h2" sx={{ mt: 1, fontSize: { xs: '2rem', md: '3rem' } }}>
              Rooted in Middlesbrough. Built around people.
            </Typography>
            <Typography sx={{ mt: 2.5, color: 'text.secondary', lineHeight: 1.8, fontSize: '1.03rem' }}>
              Founded in 2019 by Mohammad Rasheed Mahammad Riyal, MRM Lettings began with a simple belief: property services should be honest, personal and guided by genuine local knowledge. From our Middlesbrough office, the company has grown steadily while remaining true to the values that inspired the business from the beginning.
            </Typography>
            <Typography sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.8, fontSize: '1.03rem' }}>
              As a family-led company, we combine professional expertise with a personal approach so every client receives the attention, guidance and support they deserve. Mr Rasheed and his family remain actively involved in the business, working alongside a dedicated local team that understands the region, its communities and its property market.
            </Typography>
            <Typography sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.8, fontSize: '1.03rem' }}>
              Today, MRM Lettings proudly provides award-winning service to buyers, sellers, landlords and tenants across the region. Whether you are buying your first home, growing an investment portfolio, selling a property or searching for somewhere to rent, our aim is to make every step straightforward, transparent and as stress-free as possible.
            </Typography>
            <Typography sx={{ mt: 2.5, color: '#10234b', lineHeight: 1.7, fontSize: '1.08rem', fontWeight: 750 }}>
              We do more than help people move—we build lasting relationships founded on trust, integrity and exceptional service.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, bgcolor: '#f5f7fb', border: '1px solid #e2e8f0' }}>
              <HomeWorkOutlined sx={{ color: 'primary.main', fontSize: 34 }} />
              <Typography variant="overline" color="primary.main">Family-led since 2019</Typography>
              <Typography variant="h5" sx={{ mt: .5 }}>Our Middlesbrough home</Typography>
              <Typography sx={{ mt: 1.5, color: 'text.secondary', lineHeight: 1.7 }}>
                142 Victoria Road<br />Middlesbrough<br />TS1 3HU
              </Typography>
              <Typography sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.7 }}>
                One local team supporting buyers, sellers, landlords and tenants across Middlesbrough and the wider region.
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 2.5 }}>
                <Typography component="a" href="tel:+441642243378" sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 700 }}>01642 243 378</Typography>
                <Typography component="a" href="mailto:info@mrmlettings.co.uk" sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 700 }}>info@mrmlettings.co.uk</Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Box component="section" id="team" sx={{ bgcolor: '#f6f8fb', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography variant="overline" color="primary.main">What clients can expect</Typography>
          <Typography variant="h2" sx={{ mt: 1, maxWidth: 700, fontSize: { xs: '2rem', md: '2.75rem' } }}>
            Professional service with a local point of contact
          </Typography>
          <Grid container spacing={2.5} sx={{ mt: 3 }}>
            {principles.map((item) => (
              <Grid key={item.title} size={{ xs: 12, md: 4 }}>
                <Paper elevation={0} sx={{ height: '100%', p: 3.5, border: '1px solid #e2e8f0' }}>
                  <Box sx={{ width: 48, height: 48, display: 'grid', placeItems: 'center', borderRadius: 2, bgcolor: '#e9eefc', color: 'primary.main' }}>{item.icon}</Box>
                  <Typography variant="h5" sx={{ mt: 2.5 }}>{item.title}</Typography>
                  <Typography sx={{ mt: 1.25, color: 'text.secondary', lineHeight: 1.7 }}>{item.text}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Box sx={{ p: { xs: 3.5, md: 5 }, bgcolor: '#10234b', color: '#fff', borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={3 }>
            <Box>
              <Typography variant="h3" sx={{ color: '#fff', fontSize: { xs: '1.8rem', md: '2.35rem' } }}>How can we help?</Typography>
              <Typography sx={{ mt: 1, color: 'rgba(255,255,255,.72)' }}>Speak to our office about your next property decision.</Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <Button component={RouterLink} to="/contact" variant="contained" sx={{ bgcolor: '#fff', color: '#10234b', '&:hover': { bgcolor: '#edf2f7' } }}>Contact us</Button>
              <Button component={RouterLink} to="/properties" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,.5)' }}>View properties</Button>
            </Stack>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
